/**
 * User Context Enrichment for CloudWatch RUM
 *
 * This module captures comprehensive user context data including:
 * - Device information (browser, OS, screen resolution)
 * - Network information (connection type, effective type, IP via external service)
 * - Geographic information (timezone, language, geolocation if permitted)
 * - Session information (referrer, user agent)
 * - Hardware capabilities (CPU cores, device memory, GPU)
 *
 * Note: IP address and approximate location can be captured via external IP lookup service.
 * Precise geolocation requires user permission via browser Geolocation API.
 */

export interface UserContextData {
  // Raw User Agent String
  userAgent?: string;

  // Browser Information
  browser?: {
    name?: string;
    version?: string;
    platform?: string;
    vendor?: string;
    vendorSub?: string;
    language?: string;
    cookiesEnabled?: boolean;
    doNotTrack?: string | null;
    pdfViewerEnabled?: boolean;
    appName?: string;
    appVersion?: string;
    appCodeName?: string;
    product?: string;
    productSub?: string;
    buildId?: string;
  };

  // User Agent Client Hints (modern browsers)
  userAgentData?: {
    brands?: Array<{ brand: string; version: string }>;
    mobile?: boolean;
    platform?: string;
  };

  // Device Information
  device?: {
    type?: string; // mobile, tablet, desktop
    vendor?: string;
    model?: string;
    cpuCores?: number;
    deviceMemory?: number; // GB
    hardwareConcurrency?: number;
    maxTouchPoints?: number;
  };

  // Screen Information
  screen?: {
    width?: number;
    height?: number;
    availWidth?: number;
    availHeight?: number;
    availTop?: number;
    availLeft?: number;
    colorDepth?: number;
    pixelDepth?: number;
    pixelRatio?: number;
    devicePixelRatio?: number;
    orientation?: string;
    orientationAngle?: number;
  };

  // Window Information
  window?: {
    innerWidth?: number;
    innerHeight?: number;
    outerWidth?: number;
    outerHeight?: number;
    screenX?: number;
    screenY?: number;
    screenLeft?: number;
    screenTop?: number;
    scrollX?: number;
    scrollY?: number;
    devicePixelRatio?: number;
  };

  // Viewport (alias for common usage)
  viewport?: {
    width?: number;
    height?: number;
  };

  // Operating System
  os?: {
    name?: string;
    version?: string;
  };

  // Network Information
  network?: {
    effectiveType?: string; // 4g, 3g, 2g, slow-2g
    downlink?: number;
    rtt?: number;
    saveData?: boolean;
    type?: string; // wifi, cellular, ethernet, etc.
    onLine?: boolean;
  };

  // IP Location from external service (ipapi.co)
  ipLocation?: {
    ip?: string;
    city?: string;
    region?: string;
    regionCode?: string;
    country?: string;
    countryCode?: string;
    continent?: string;
    postalCode?: string;
    latitude?: number;
    longitude?: number;
    timezone?: string;
    currency?: string;
    isp?: string;
    asn?: string;
  };

  // Browser Geolocation (if user grants permission)
  geolocation?: {
    latitude?: number;
    longitude?: number;
    accuracy?: number;
    altitude?: number;
    altitudeAccuracy?: number;
    heading?: number;
    speed?: number;
    timestamp?: number;
    permissionState?: string; // granted, denied, prompt
    permissionDenied?: boolean;
    errorCode?: number;
    errorMessage?: string;
  };

  // Geographic/Locale Information
  locale?: {
    language?: string;
    languages?: string[];
    timezone?: string;
    timezoneOffset?: number;
    estimatedRegion?: string;
    calendar?: string;
    numberingSystem?: string;
  };

  // Document Information
  document?: {
    title?: string;
    characterSet?: string;
    contentType?: string;
    referrer?: string;
    lastModified?: string;
    visibilityState?: string;
    hidden?: boolean;
    documentMode?: number; // IE
    compatMode?: string;
    designMode?: string;
  };

  // Session/Navigation Information
  session?: {
    referrer?: string;
    referrerDomain?: string;
    landingPage?: string;
    currentPage?: string;
    currentUrl?: string;
    queryParams?: string;
    hash?: string;
    hostname?: string;
    protocol?: string;
    port?: string;
    origin?: string;
    sessionStart?: string;
    entryType?: string; // direct, organic, referral, social, etc.
    navigationType?: string; // navigate, reload, back_forward, prerender
    historyLength?: number;
  };

  // Performance/Memory
  memory?: {
    jsHeapSizeLimit?: number;
    totalJSHeapSize?: number;
    usedJSHeapSize?: number;
  };

  // Page Performance Timing
  performance?: {
    loadTime?: number;
    domContentLoaded?: number;
    firstPaint?: number;
    firstContentfulPaint?: number;
    domInteractive?: number;
    domComplete?: number;
    redirectCount?: number;
    navigationType?: string;
  };

  // Storage Information
  storage?: {
    quota?: number;
    usage?: number;
    persistent?: boolean;
  };

  // Accessibility/Features
  features?: {
    cookiesEnabled?: boolean;
    doNotTrack?: boolean;
    onLine?: boolean;
    touchEnabled?: boolean;
    webGL?: boolean;
    serviceWorker?: boolean;
    localStorage?: boolean;
    sessionStorage?: boolean;
    webSockets?: boolean;
    pdfViewerEnabled?: boolean;
    webdriver?: boolean;
    javaEnabled?: boolean;
  };

  // Browser Capabilities
  capabilities?: {
    webGL?: boolean;
    webGL2?: boolean;
    serviceWorker?: boolean;
    webWorker?: boolean;
    sharedWorker?: boolean;
    localStorage?: boolean;
    sessionStorage?: boolean;
    indexedDB?: boolean;
    webSockets?: boolean;
    pdfViewerEnabled?: boolean;
    bluetooth?: boolean;
    usb?: boolean;
    midi?: boolean;
    geolocation?: boolean;
    notifications?: boolean;
    pushManager?: boolean;
    mediaDevices?: boolean;
    clipboard?: boolean;
    share?: boolean;
    wakeLock?: boolean;
    vibrate?: boolean;
    speechSynthesis?: boolean;
    speechRecognition?: boolean;
  };

  // Hardware/GPU
  hardware?: {
    gpuVendor?: string;
    gpuRenderer?: string;
    maxTextureSize?: number;
  };

  // Battery (if available)
  battery?: {
    level?: number;
    charging?: boolean;
    chargingTime?: number;
    dischargingTime?: number;
  };

  // Raw navigator info (all properties)
  navigatorRaw?: Record<string, any>;

  // Date/Time Context
  dateTime?: {
    timestamp?: string;
    localTime?: string;
    utcTime?: string;
    timezoneOffset?: number;
    dayOfWeek?: number;
    isWeekend?: boolean;
  };
}

/**
 * Detect browser name and version from user agent
 */
function detectBrowser(): {
  name?: string;
  version?: string;
  platform?: string;
} {
  if (typeof window === "undefined" || !navigator) return {};

  const ua = navigator.userAgent;
  let name = "Unknown";
  let version = "Unknown";
  let platform = navigator.platform;

  // Detect browser
  if (ua.indexOf("Firefox") > -1) {
    name = "Firefox";
    const match = ua.match(/Firefox\/(\d+\.\d+)/);
    version = match ? match[1] : "Unknown";
  } else if (ua.indexOf("Edg") > -1) {
    name = "Edge";
    const match = ua.match(/Edg\/(\d+\.\d+)/);
    version = match ? match[1] : "Unknown";
  } else if (ua.indexOf("Chrome") > -1) {
    name = "Chrome";
    const match = ua.match(/Chrome\/(\d+\.\d+)/);
    version = match ? match[1] : "Unknown";
  } else if (ua.indexOf("Safari") > -1) {
    name = "Safari";
    const match = ua.match(/Version\/(\d+\.\d+)/);
    version = match ? match[1] : "Unknown";
  } else if (ua.indexOf("MSIE") > -1 || ua.indexOf("Trident") > -1) {
    name = "IE";
    const match = ua.match(/(?:MSIE |rv:)(\d+\.\d+)/);
    version = match ? match[1] : "Unknown";
  }

  return { name, version, platform };
}

/**
 * Detect operating system
 */
function detectOS(): { name?: string; version?: string } {
  if (typeof window === "undefined" || !navigator) return {};

  const ua = navigator.userAgent;
  let name = "Unknown";
  let version = "Unknown";

  if (ua.indexOf("Win") > -1) {
    name = "Windows";
    if (ua.indexOf("Windows NT 10.0") > -1) version = "10";
    else if (ua.indexOf("Windows NT 6.3") > -1) version = "8.1";
    else if (ua.indexOf("Windows NT 6.2") > -1) version = "8";
    else if (ua.indexOf("Windows NT 6.1") > -1) version = "7";
  } else if (ua.indexOf("Mac") > -1) {
    name = "macOS";
    const match = ua.match(/Mac OS X (\d+[._]\d+[._]\d+)/);
    version = match ? match[1].replace(/_/g, ".") : "Unknown";
  } else if (ua.indexOf("Linux") > -1) {
    name = "Linux";
  } else if (ua.indexOf("Android") > -1) {
    name = "Android";
    const match = ua.match(/Android (\d+\.\d+)/);
    version = match ? match[1] : "Unknown";
  } else if (
    ua.indexOf("iOS") > -1 ||
    ua.indexOf("iPhone") > -1 ||
    ua.indexOf("iPad") > -1
  ) {
    name = "iOS";
    const match = ua.match(/OS (\d+_\d+)/);
    version = match ? match[1].replace(/_/g, ".") : "Unknown";
  }

  return { name, version };
}

/**
 * Detect device type
 */
function detectDevice(): { type?: string; vendor?: string; model?: string } {
  if (typeof window === "undefined" || !navigator) return {};

  const ua = navigator.userAgent;
  let type = "desktop";

  // Check for mobile/tablet
  if (/Mobi|Android/i.test(ua)) {
    type = "mobile";
  } else if (/Tablet|iPad/i.test(ua)) {
    type = "tablet";
  }

  // Try to detect vendor
  let vendor = "Unknown";
  if (ua.indexOf("Samsung") > -1) vendor = "Samsung";
  else if (ua.indexOf("iPhone") > -1 || ua.indexOf("iPad") > -1)
    vendor = "Apple";
  else if (ua.indexOf("Pixel") > -1) vendor = "Google";
  else if (ua.indexOf("OnePlus") > -1) vendor = "OnePlus";

  return { type, vendor };
}

/**
 * Get network information (if available)
 */
function getNetworkInfo(): UserContextData["network"] {
  if (typeof window === "undefined" || !navigator) return undefined;

  // @ts-ignore - NetworkInformation is not in all browsers
  const connection =
    (navigator as any).connection ||
    (navigator as any).mozConnection ||
    (navigator as any).webkitConnection;

  if (!connection) return undefined;

  return {
    effectiveType: connection.effectiveType,
    downlink: connection.downlink,
    rtt: connection.rtt,
    saveData: connection.saveData,
  };
}

/**
 * Get memory information (if available)
 */
function getMemoryInfo(): UserContextData["memory"] {
  if (typeof window === "undefined") return undefined;

  // @ts-ignore - performance.memory is Chrome-specific
  const memory = (performance as any).memory;

  if (!memory) return undefined;

  return {
    jsHeapSizeLimit: memory.jsHeapSizeLimit,
    totalJSHeapSize: memory.totalJSHeapSize,
    usedJSHeapSize: memory.usedJSHeapSize,
  };
}

/**
 * Get battery information (if available)
 */
async function getBatteryInfo(): Promise<UserContextData["battery"] | undefined> {
  if (typeof window === "undefined" || !navigator) return undefined;

  try {
    // @ts-ignore - getBattery is not in all browsers
    if ("getBattery" in navigator) {
      // @ts-ignore
      const battery = await navigator.getBattery();
      return {
        level: battery.level,
        charging: battery.charging,
        chargingTime: battery.chargingTime === Infinity ? undefined : battery.chargingTime,
        dischargingTime: battery.dischargingTime === Infinity ? undefined : battery.dischargingTime,
      };
    }
  } catch (e) {
    console.debug("Battery API not available:", e);
  }
  return undefined;
}

/**
 * Get GPU/hardware information via WebGL
 */
function getHardwareInfo(): UserContextData["hardware"] {
  if (typeof window === "undefined") return undefined;

  try {
    const canvas = document.createElement("canvas");
    const gl =
      canvas.getContext("webgl") || canvas.getContext("experimental-webgl");

    if (gl && gl instanceof WebGLRenderingContext) {
      const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
      if (debugInfo) {
        return {
          gpuVendor: gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL),
          gpuRenderer: gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL),
          maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE),
        };
      }
      return {
        maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE),
      };
    }
  } catch (e) {
    console.debug("GPU info not available:", e);
  }
  return undefined;
}

/**
 * Get estimated location from timezone (no permission needed)
 */
function getTimezoneLocation(): {
  timezone: string;
  estimatedRegion?: string;
  utcOffset: number;
} {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const utcOffset = new Date().getTimezoneOffset();

  // Extract region from timezone (e.g., "America/New_York" -> "America")
  const parts = timezone.split("/");
  const estimatedRegion = parts.length > 0 ? parts[0] : undefined;

  return { timezone, estimatedRegion, utcOffset };
}

/**
 * Get geolocation (requires permission)
 * Returns a promise that resolves with location or undefined
 */
export async function captureGeolocation(): Promise<
  UserContextData["geolocation"] | undefined
> {
  if (typeof window === "undefined" || !navigator.geolocation) {
    return undefined;
  }

  return new Promise((resolve) => {
    // Set a timeout so we don't wait forever
    const timeoutId = setTimeout(() => {
      console.debug("Geolocation timed out");
      resolve(undefined);
    }, 10000);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        clearTimeout(timeoutId);
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          altitude: position.coords.altitude || undefined,
          altitudeAccuracy: position.coords.altitudeAccuracy || undefined,
          heading: position.coords.heading || undefined,
          speed: position.coords.speed || undefined,
          timestamp: position.timestamp,
        });
      },
      (error) => {
        clearTimeout(timeoutId);
        console.debug("Geolocation error:", error.message);
        // Return permission status even on error
        resolve({
          permissionDenied: error.code === error.PERMISSION_DENIED,
          errorCode: error.code,
          errorMessage: error.message,
        } as any);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000, // Cache for 5 minutes
      }
    );
  });
}

/**
 * Fetch approximate IP location from a free service (no permission needed)
 * This is async and should be called separately
 */
export async function fetchIpLocation(): Promise<
  UserContextData["ipLocation"] | undefined
> {
  try {
    // Using ipapi.co - free tier allows 1000 requests/day
    const response = await fetch("https://ipapi.co/json/", {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });

    if (!response.ok) {
      console.debug("IP location fetch failed:", response.status);
      return undefined;
    }

    const data = await response.json();

    return {
      ip: data.ip,
      city: data.city,
      region: data.region,
      regionCode: data.region_code,
      country: data.country_name,
      countryCode: data.country_code,
      continent: data.continent_code,
      postalCode: data.postal,
      latitude: data.latitude,
      longitude: data.longitude,
      timezone: data.timezone,
      currency: data.currency,
      isp: data.org,
      asn: data.asn,
    };
  } catch (e) {
    console.debug("IP location fetch error:", e);
    return undefined;
  }
}

/**
 * Get all navigator properties available
 */
function getNavigatorInfo(): Record<string, any> {
  if (typeof window === "undefined" || !navigator) return {};

  const nav: Record<string, any> = {};

  // Standard properties
  const props = [
    "appCodeName",
    "appName",
    "appVersion",
    "cookieEnabled",
    "deviceMemory",
    "doNotTrack",
    "hardwareConcurrency",
    "language",
    "maxTouchPoints",
    "onLine",
    "pdfViewerEnabled",
    "platform",
    "product",
    "productSub",
    "userAgent",
    "vendor",
    "vendorSub",
    "webdriver",
  ];

  for (const prop of props) {
    try {
      const value = (navigator as any)[prop];
      if (value !== undefined && value !== null && value !== "") {
        nav[prop] = value;
      }
    } catch (e) {
      // Skip properties that throw
    }
  }

  // languages array
  if (navigator.languages) {
    nav.languages = Array.from(navigator.languages);
  }

  return nav;
}

/**
 * Get all screen properties
 */
function getFullScreenInfo(): UserContextData["screen"] {
  if (typeof window === "undefined" || !window.screen) return undefined;

  return {
    width: window.screen.width,
    height: window.screen.height,
    availWidth: window.screen.availWidth,
    availHeight: window.screen.availHeight,
    availTop: (window.screen as any).availTop,
    availLeft: (window.screen as any).availLeft,
    colorDepth: window.screen.colorDepth,
    pixelDepth: window.screen.pixelDepth,
    devicePixelRatio: window.devicePixelRatio,
    orientation: window.screen.orientation?.type,
    orientationAngle: window.screen.orientation?.angle,
  };
}

/**
 * Get window properties
 */
function getWindowInfo(): UserContextData["window"] {
  if (typeof window === "undefined") return undefined;

  return {
    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight,
    outerWidth: window.outerWidth,
    outerHeight: window.outerHeight,
    screenX: window.screenX,
    screenY: window.screenY,
    screenLeft: window.screenLeft,
    screenTop: window.screenTop,
    scrollX: window.scrollX,
    scrollY: window.scrollY,
    devicePixelRatio: window.devicePixelRatio,
  };
}

/**
 * Get document properties
 */
function getDocumentInfo(): UserContextData["document"] {
  if (typeof document === "undefined") return undefined;

  return {
    title: document.title,
    characterSet: document.characterSet,
    contentType: document.contentType,
    referrer: document.referrer || undefined,
    lastModified: document.lastModified,
    visibilityState: document.visibilityState,
    hidden: document.hidden,
    documentMode: (document as any).documentMode, // IE
    compatMode: document.compatMode,
    designMode: document.designMode,
  };
}

/**
 * Get User Agent Client Hints (modern browsers)
 */
function getUserAgentData(): UserContextData["userAgentData"] {
  if (typeof navigator === "undefined") return undefined;

  const uaData = (navigator as any).userAgentData;
  if (!uaData) return undefined;

  return {
    brands: uaData.brands?.map((b: any) => ({ brand: b.brand, version: b.version })),
    mobile: uaData.mobile,
    platform: uaData.platform,
  };
}

/**
 * Get comprehensive browser capabilities (no permission needed)
 */
function getAllCapabilities(): UserContextData["capabilities"] {
  if (typeof window === "undefined" || typeof navigator === "undefined") return undefined;

  return {
    webGL: (() => {
      try {
        const canvas = document.createElement("canvas");
        return !!(
          window.WebGLRenderingContext &&
          (canvas.getContext("webgl") || canvas.getContext("experimental-webgl"))
        );
      } catch (e) {
        return false;
      }
    })(),
    webGL2: (() => {
      try {
        const canvas = document.createElement("canvas");
        return !!(window.WebGL2RenderingContext && canvas.getContext("webgl2"));
      } catch (e) {
        return false;
      }
    })(),
    serviceWorker: "serviceWorker" in navigator,
    webWorker: typeof Worker !== "undefined",
    sharedWorker: typeof SharedWorker !== "undefined",
    localStorage: (() => {
      try {
        return typeof localStorage !== "undefined";
      } catch (e) {
        return false;
      }
    })(),
    sessionStorage: (() => {
      try {
        return typeof sessionStorage !== "undefined";
      } catch (e) {
        return false;
      }
    })(),
    indexedDB: "indexedDB" in window,
    webSockets: "WebSocket" in window,
    pdfViewerEnabled: (navigator as any).pdfViewerEnabled,
    bluetooth: "bluetooth" in navigator,
    usb: "usb" in navigator,
    midi: "requestMIDIAccess" in navigator,
    geolocation: "geolocation" in navigator,
    notifications: "Notification" in window,
    pushManager: "PushManager" in window,
    mediaDevices: "mediaDevices" in navigator,
    clipboard: "clipboard" in navigator,
    share: "share" in navigator,
    wakeLock: "wakeLock" in navigator,
    vibrate: "vibrate" in navigator,
    speechSynthesis: "speechSynthesis" in window,
    speechRecognition: "webkitSpeechRecognition" in window || "SpeechRecognition" in window,
  };
}

/**
 * Get performance timing data
 */
function getPerformanceInfo(): UserContextData["performance"] {
  if (typeof window === "undefined" || typeof performance === "undefined") return undefined;

  const timing = (performance as any).timing;
  const navigation = (performance as any).navigation;

  if (!timing) return undefined;

  return {
    loadTime: timing.loadEventEnd - timing.navigationStart,
    domContentLoaded: timing.domContentLoadedEventEnd - timing.navigationStart,
    domInteractive: timing.domInteractive - timing.navigationStart,
    domComplete: timing.domComplete - timing.navigationStart,
    redirectCount: navigation?.redirectCount,
    navigationType: navigation?.type === 0 ? "navigate" :
                    navigation?.type === 1 ? "reload" :
                    navigation?.type === 2 ? "back_forward" :
                    navigation?.type === 255 ? "prerender" : undefined,
  };
}

/**
 * Get date/time context
 */
function getDateTimeInfo(): UserContextData["dateTime"] {
  const now = new Date();
  const dayOfWeek = now.getDay();

  return {
    timestamp: now.toISOString(),
    localTime: now.toLocaleString(),
    utcTime: now.toUTCString(),
    timezoneOffset: now.getTimezoneOffset(),
    dayOfWeek,
    isWeekend: dayOfWeek === 0 || dayOfWeek === 6,
  };
}

/**
 * Get locale information with Intl API
 */
function getFullLocaleInfo(): UserContextData["locale"] {
  if (typeof Intl === "undefined") return undefined;

  const resolvedOptions = Intl.DateTimeFormat().resolvedOptions();
  const timezone = resolvedOptions.timeZone;
  const parts = timezone.split("/");

  return {
    language: navigator.language,
    languages: navigator.languages ? Array.from(navigator.languages) : undefined,
    timezone,
    timezoneOffset: new Date().getTimezoneOffset(),
    estimatedRegion: parts.length > 0 ? parts[0] : undefined,
    calendar: resolvedOptions.calendar,
    numberingSystem: resolvedOptions.numberingSystem,
  };
}

/**
 * Capture comprehensive user context
 * This function should be called once when RUM initializes
 */
export function captureUserContext(): UserContextData {
  if (typeof window === "undefined" || !navigator) {
    return {};
  }

  const browser = detectBrowser();
  const os = detectOS();
  const device = detectDevice();
  const network = getNetworkInfo();
  const memory = getMemoryInfo();
  const capabilities = getAllCapabilities();
  const hardware = getHardwareInfo();
  const navigatorInfo = getNavigatorInfo();
  const screenInfo = getFullScreenInfo();
  const windowInfo = getWindowInfo();
  const documentInfo = getDocumentInfo();
  const userAgentData = getUserAgentData();
  const performanceInfo = getPerformanceInfo();
  const dateTimeInfo = getDateTimeInfo();
  const localeInfo = getFullLocaleInfo();

  // Navigation type
  const navType = (performance as any)?.navigation?.type;
  const navigationType = navType === 0 ? "navigate" :
                         navType === 1 ? "reload" :
                         navType === 2 ? "back_forward" :
                         navType === 255 ? "prerender" : "unknown";

  const context: UserContextData = {
    // Raw user agent string
    userAgent: navigator.userAgent,

    // Browser info
    browser: {
      ...browser,
      vendor: navigator.vendor,
      vendorSub: (navigator as any).vendorSub,
      cookiesEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack,
      pdfViewerEnabled: (navigator as any).pdfViewerEnabled,
      appName: navigator.appName,
      appVersion: navigator.appVersion,
      appCodeName: navigator.appCodeName,
      product: navigator.product,
      productSub: (navigator as any).productSub,
      buildId: (navigator as any).buildID,
    },

    // User Agent Client Hints
    userAgentData,

    // Device info
    device: {
      ...device,
      hardwareConcurrency: navigator.hardwareConcurrency,
      deviceMemory: (navigator as any).deviceMemory,
      maxTouchPoints: navigator.maxTouchPoints,
    },

    // OS info
    os,

    // Screen info
    screen: screenInfo,

    // Window info
    window: windowInfo,

    // Viewport (convenience)
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
    },

    // Network info
    network: {
      ...network,
      onLine: navigator.onLine,
    },

    // Memory
    memory,

    // Capabilities
    capabilities,

    // Hardware/GPU
    hardware,

    // Locale
    locale: localeInfo,

    // Document
    document: documentInfo,

    // Session info
    session: {
      referrer: document.referrer || undefined,
      referrerDomain: (() => {
        try {
          return document.referrer ? new URL(document.referrer).hostname : undefined;
        } catch (e) {
          return undefined;
        }
      })(),
      landingPage: window.location.href,
      currentPage: window.location.pathname,
      currentUrl: window.location.href,
      queryParams: window.location.search || undefined,
      hash: window.location.hash || undefined,
      hostname: window.location.hostname,
      protocol: window.location.protocol,
      port: window.location.port || undefined,
      origin: window.location.origin,
      sessionStart: new Date().toISOString(),
      navigationType,
      historyLength: window.history.length,
    },

    // Performance
    performance: performanceInfo,

    // Features
    features: {
      cookiesEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack === "1",
      onLine: navigator.onLine,
      touchEnabled: "ontouchstart" in window || navigator.maxTouchPoints > 0,
      webdriver: (navigator as any).webdriver || false,
      javaEnabled: typeof navigator.javaEnabled === "function" ? navigator.javaEnabled() : false,
    },

    // Date/Time
    dateTime: dateTimeInfo,

    // Raw navigator info for completeness
    navigatorRaw: navigatorInfo,
  };

  return context;
}

/**
 * Get a minimal subset of user context for every event
 */
export function getMinimalUserContext(): Partial<UserContextData> {
  if (typeof window === "undefined") return {};

  return {
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
    session: {
      currentPage: window.location.pathname,
    },
    features: {
      onLine: navigator.onLine,
    },
  };
}

/**
 * Global user context cache
 */
let userContextCache: UserContextData | null = null;

/**
 * Get cached user context or capture it
 */
export function getUserContext(): UserContextData {
  if (!userContextCache) {
    userContextCache = captureUserContext();
  }
  return userContextCache;
}

/**
 * Update session page context (call on route changes)
 */
export function updateSessionContext() {
  if (userContextCache && typeof window !== "undefined") {
    userContextCache.session = {
      ...userContextCache.session,
      currentPage: window.location.pathname,
    };
  }
}
